import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { DRYBULK_CARGO_OPS_ROUTE_SEGMENTS } from "@features/drybulk-cargo-operations/constants";
import { useIsCargoOpsShippingAgent } from "@features/drybulk-cargo-operations/hooks/useIsCargoOpsShippingAgent";
import { cargoOpsDetailPath } from "@features/drybulk-cargo-operations/utils/cargoOpsPaths";
import type { VesselCallStatusContextType } from "@features/vessel-call/vessel-call-status/VesselCallStatus";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";

const LANG_BASE = "vessel-call.operations";
const DRY_BULK_OPERATION_PATTERN = `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/:id/${paths.vesselCall.operations.index}/${paths.vesselCall.operations.dryBulk}/:operationId/*`;

type OperationTab = {
  labelKey: string;
  path: string;
  testId: string;
};

const OperationsContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext<VesselCallStatusContextType>();
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const isOperationsUser = useAuthStore((state) => state.isOperationsUser);
  const {
    canAccessOperations,
    canSeeOperationsDisputes,
    canSeeOperationsLiquidBulk,
    canSeeOperationsDryBulk,
    isOperationsAccessLoading,
  } = outletContext;

  const operationTabs: OperationTab[] = [];

  if (canSeeOperationsDisputes) {
    operationTabs.push({
      labelKey: `${LANG_BASE}.disputes`,
      path: paths.vesselCall.operations.disputes,
      testId: "operations-tab-disputes",
    });
  }

  if (canSeeOperationsLiquidBulk) {
    operationTabs.push({
      labelKey: `${LANG_BASE}.liquid-bulk`,
      path: paths.vesselCall.operations.liquidBulk,
      testId: "operations-tab-liquid-bulk",
    });
  }

  if (canSeeOperationsDryBulk) {
    operationTabs.push({
      labelKey: `${LANG_BASE}.dry-bulk`,
      path: paths.vesselCall.operations.dryBulk,
      testId: "operations-tab-dry-bulk",
    });
  }

  const activeTab = operationTabs.findIndex((tab) => location.pathname.includes(`/${tab.path}`));
  const currentTab = activeTab === -1 ? 0 : activeTab;

  if (isOperationsAccessLoading) {
    return <LoadingOverlay loading={isOperationsAccessLoading} />;
  }

  const operationId = matchPath(DRY_BULK_OPERATION_PATTERN, location.pathname)?.params.operationId;
  if (
    isShippingAgent &&
    !isOperationsUser &&
    operationId &&
    operationId !== DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.addOperation &&
    (!canAccessOperations || !canSeeOperationsDryBulk)
  ) {
    return <Navigate replace to={cargoOpsDetailPath(operationId)} />;
  }

  if (!canAccessOperations) {
    return <UnauthorizedRedirection />;
  }

  return (
    <div className="w-full">
      <Tabs
        value={currentTab}
        onChange={(_, index: number) => navigate(operationTabs[index].path)}
        aria-label={t(`${LANG_BASE}.tabs-label`)}
        sx={{
          "borderBottom": "1px solid var(--stroke-secondary-stroke, #E6EDF2)",
          "minHeight": 40,
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "1rem",
            minHeight: 40,
            minWidth: "auto",
            px: 2,
          },
        }}
      >
        {operationTabs.map((tab) => (
          <Tab key={tab.path} label={t(tab.labelKey)} data-testid={tab.testId} />
        ))}
      </Tabs>

      <div className="pt-4">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};

export default OperationsContainer;
